import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyFocusZoom,
  findFocusObjects,
  getHeroScrollProgress,
} from './heroSplineFocus';
import { getSplineLandingScene, isReactSplineScene } from './splineScenes';
import './Hero.css';

const HeroSpline = lazy(() => import('./HeroSpline'));

const MOBILE_SPLINE_MQ = '(max-width: 768px)';
const MOBILE_SPLINE_ZOOM_DEFAULT = 0.6;
// Chrome evicts WebGL contexts under memory pressure, and this page runs
// several WebGL scenes at once (hero, product carousel, contact). A dropped
// context leaves the canvas frozen on its last frame forever, so remount the
// scene when that happens. Capped so a scene that cannot hold a context
// doesn't remount in a loop.
const MAX_CONTEXT_RECOVERIES = 3;

const isMobileSplineViewport = () =>
  window.matchMedia(MOBILE_SPLINE_MQ).matches;

const getSplineZoom = () => {
  const desktopZoom = Number(process.env.REACT_APP_SPLINE_ZOOM) || 0.85;

  if (isMobileSplineViewport()) {
    const mobileZoom = Number(process.env.REACT_APP_SPLINE_ZOOM_MOBILE);
    return Number.isFinite(mobileZoom) && mobileZoom > 0
      ? mobileZoom
      : MOBILE_SPLINE_ZOOM_DEFAULT;
  }

  return desktopZoom;
};

const fitSplineToScreen = (app, heroEl) => {
  const rect = heroEl?.getBoundingClientRect?.();
  const width =
    rect?.width || window.visualViewport?.width || window.innerWidth;
  const height =
    rect?.height || window.visualViewport?.height || window.innerHeight;

  if (width > 0 && height > 0) {
    app.setSize(width, height);
  }
  app.setZoom(getSplineZoom());
};

export default function Hero() {
  const { i18n } = useTranslation();
  const heroRef = useRef(null);
  const appRef = useRef(null);
  const focusTargetsRef = useRef([]);

  const [language, setLanguage] = useState(
    () => i18n.resolvedLanguage || i18n.language || 'en'
  );
  const [allowHeavyScene, setAllowHeavyScene] = useState(false);
  const [recoverNonce, setRecoverNonce] = useState(0);
  const recoverAttemptsRef = useRef(0);
  const contextCleanupRef = useRef(null);
  const [lightRender, setLightRender] = useState(false);

  const scene = useMemo(
    () => getSplineLandingScene(language),
    [language]
  );

  const useReactSpline = isReactSplineScene(scene);

  useEffect(() => {
    const syncLanguage = (lng) => {
      setLanguage(lng || i18n.resolvedLanguage || i18n.language || 'en');
    };

    syncLanguage(i18n.resolvedLanguage || i18n.language);
    i18n.on('languageChanged', syncLanguage);

    return () => {
      i18n.off('languageChanged', syncLanguage);
    };
  }, [i18n]);

  // Keep Spline on all viewports, but start it after first paint so FCP/LCP stay fast.
  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      return undefined;
    }

    let cancelled = false;
    const enable = () => {
      if (cancelled) return;
      setLightRender(isMobileSplineViewport());
      setAllowHeavyScene(true);
    };

    const isMobile = window.matchMedia(MOBILE_SPLINE_MQ).matches;
    // Mobile: wait a bit longer so text/LCP can paint before Three.js boots.
    const idleTimeout = isMobile ? 2800 : 1600;
    const fallbackDelay = isMobile ? 1600 : 800;

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(enable, { timeout: idleTimeout });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(idleId);
      };
    }

    const timerId = window.setTimeout(enable, fallbackDelay);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, []);

  // NOTE: do not call app.stop() to save GPU while the hero is offscreen.
  // In @splinetool/runtime 1.12.94 stop() -> eventManager.pause() wipes the
  // scene's tweens array (`this.tweens = Array(len - 1)`) and resume() never
  // rebuilds it. The render loop restarts on play(), but the scene animation
  // is dead for good — the hero renders a frozen pose after any scroll away
  // and back. Leaving the scene running is the only correct option here.

  const syncFocusZoom = useCallback(() => {
    const hero = heroRef.current;
    const app = appRef.current;
    const targets = focusTargetsRef.current;
    if (!hero || !app || !targets.length) return;

    requestAnimationFrame(() => {
      const progress = getHeroScrollProgress(hero);
      applyFocusZoom(targets, progress);
      app.requestRender?.();
    });
  }, []);

  const onLoad = useCallback(
    (app) => {
      appRef.current = app;
      if (process.env.NODE_ENV === 'development') {
        window.__heroApp = app;
      }

      contextCleanupRef.current?.();
      contextCleanupRef.current = null;
      const canvas = heroRef.current?.querySelector('canvas');
      if (canvas) {
        const onContextLost = (event) => {
          // Preventing the default is what makes the context restorable.
          event.preventDefault();
          appRef.current = null;
          focusTargetsRef.current = [];
          if (recoverAttemptsRef.current >= MAX_CONTEXT_RECOVERIES) return;
          recoverAttemptsRef.current += 1;
          setRecoverNonce((nonce) => nonce + 1);
        };
        canvas.addEventListener('webglcontextlost', onContextLost);
        contextCleanupRef.current = () => {
          canvas.removeEventListener('webglcontextlost', onContextLost);
        };
      }
      // Cap DPR on mobile to cut GPU/fill-rate cost without removing the scene.
      if (isMobileSplineViewport() && typeof app.setPixelRatio === 'function') {
        app.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25));
      }
      fitSplineToScreen(app, heroRef.current);
      focusTargetsRef.current = findFocusObjects(app, language);
      syncFocusZoom();
    },
    [syncFocusZoom, language]
  );

  useEffect(() => {
    appRef.current = null;
    focusTargetsRef.current = [];
  }, [language, scene]);

  useEffect(
    () => () => {
      contextCleanupRef.current?.();
      contextCleanupRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!useReactSpline) return undefined;

    let rafId = null;

    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        syncFocusZoom();
        rafId = null;
      });
    };

    const onResize = () => {
      if (appRef.current) {
        fitSplineToScreen(appRef.current, heroRef.current);
        syncFocusZoom();
      }
    };

    const mobileMq = window.matchMedia(MOBILE_SPLINE_MQ);
    const onViewportChange = () => onResize();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    mobileMq.addEventListener('change', onViewportChange);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      mobileMq.removeEventListener('change', onViewportChange);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [syncFocusZoom, useReactSpline]);

  if (!scene || !allowHeavyScene) {
    return <div ref={heroRef} className="hero-spline-inner" aria-hidden="true" />;
  }

  const splineKey = `${language}-${scene}-${recoverNonce}`;

  return (
    <div
      ref={heroRef}
      className={`hero-spline-inner${useReactSpline ? ' hero-spline-inner--react' : ''}`}
    >
      <Suspense fallback={null}>
        <HeroSpline
          key={splineKey}
          splineKey={splineKey}
          scene={scene}
          onLoad={useReactSpline ? onLoad : undefined}
          renderOnDemand={lightRender}
        />
      </Suspense>
    </div>
  );
}
